import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../useApi';
import type { EntityDefinition, EntityListResult, JobResult, WritePolicy } from '../api';
import { ErrorNotice } from '../components/ErrorNotice';
import { fieldLabel, fieldTitle, isAttribute, isNested } from '../fieldLabels';
import { awaitJob, type JobProgress } from '../awaitJob';
import { SlowHint } from '../components/SlowHint.js';
import { formatReadOnlyCell, readOnlyCellTitle } from '../cellFormat';
import { WriteBlockedNotice } from '../components/WriteBlockedNotice.js';
import { pendingEditsWarning, shouldConfirmLeave } from '../pendingEditsWarning';
import { settledEditKeys } from '../settledEdits';
import { canConfirm } from '../writeGate';
import { mergeColumnWidth } from '../columnWidths';
import { toCellText } from '../cellFormat';

// Editable grid with a preview/diff step, and the screen that starts every
// grid write.
//
// The full path: edits are tracked against the values that came back from the
// instance and survive paging and searching; Preview shows them field by field;
// Confirm POSTs a change set and gets 202 with a job id back, because the write
// runs as a Durable Functions orchestration rather than inside the request;
// awaitJob then polls until the job reaches a terminal state.
//
// Preview exists because of design principle 4: every write must be
// reversible, and the cheapest reversal is the one the user declines to make.
//
// ⚠ This paragraph said "nothing here writes to Acumatica yet — Confirm is the
// next slice" long after Confirm wrote to real customer data. Left alone it
// tells the next person that the riskiest screen in the product is inert.

const PAGE_SIZE = 50;


/**
 * Columns nobody may edit.
 *
 * The key field is excluded because changing it is a rename, not an edit —
 * Acumatica treats it as identity, and a bulk rename is a different feature
 * with different consequences. LastModifiedDateTime is the server's to write.
 */
function readOnlyColumns(keyField: string): Set<string> {
  return new Set([keyField, 'LastModifiedDateTime', 'CreatedDateTime']);
}

/*
 * Column widths.
 *
 * The natural width of each column is MEASURED from the rendered table rather
 * than estimated from character counts. An estimate was tried first and was
 * wrong in both directions — headings are uppercase and letter-spaced while
 * values are not, so 'CustomerClass' over cells reading 'KEY' clipped its own
 * heading, and the read-only marker on the key column was missed entirely.
 * The browser already knows the answer; asking it is both shorter and right.
 *
 * The measured width becomes the default, capped so that one 80-character
 * company name cannot push every other column off screen. Dragging, and
 * double-clicking to fit, are both free of that cap — an explicit "show me all
 * of it" should show all of it.
 */
const MIN_COLUMN_WIDTH = 72;
const MAX_COLUMN_WIDTH = 640;
const DEFAULT_WIDTH_CAP = 260;

const clampWidth = (px: number) =>
  Math.min(Math.max(Math.round(px), MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH);

// ⚠ The Yes/No rendering for booleans lived here and had to go: it reached the
// edits map, the diff and the job payload, so a boolean edit would have been
// refused as a phantom conflict or written as the literal string "No". See
// toCellText for the whole chain.
const formatCell = toCellText;

/** One pending change to one field of one record. */
interface FieldEdit {
  before: unknown;
  after: string;
}

/** Pending changes for one record, keyed by field name. */
interface RecordEdit {
  /** The human-visible key (CustomerID), so preview can name the record. */
  keyValue: string;
  fields: Record<string, FieldEdit>;
}

export function EntityGridPage() {
  const { tenantId, entity } = useParams<{ tenantId: string; entity: string }>();
  const api = useApi();
  const navigate = useNavigate();

  const [definition, setDefinition] = useState<EntityDefinition | null>(null);
  const [allEntities, setAllEntities] = useState<EntityDefinition[]>([]);
  /**
   * Where the user asked to go while edits were pending, and how to get there.
   *
   * Generalised from "which entity tab" so that ordinary in-app links go
   * through the same question — they unmount this page and lose the edits
   * exactly as a tab switch does, and used to do it silently.
   */
  const [pendingLeave, setPendingLeave] = useState<
    { destination: string; go: () => void } | null
  >(null);
  const [result, setResult] = useState<EntityListResult | null>(null);
  const [skip, setSkip] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [appliedFilter, setAppliedFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edits are keyed by the record's stable Acumatica guid, NOT by its position
  // or its business key: the grid repaginates and refilters underneath them,
  // and the whole point is that a change made on page 1 is still pending when
  // the user is looking at page 3.
  const [edits, setEdits] = useState<Record<string, RecordEdit>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /** Synchronous twin of `submitting` — see confirmChanges for why state is not enough. */
  const submittingRef = useRef(false);
  /** Progress of the job currently running, if any. */
  const [progress, setProgress] = useState<JobProgress | null>(null);
  const [jobResult, setJobResult] = useState<JobResult | null>(null);
  const [policy, setPolicy] = useState<WritePolicy | null>(null);

  /*
   * Hidden columns, and whether the picker is open.
   *
   * Kept per tenant AND per entity, in localStorage: which columns matter is a
   * property of the job somebody is doing on that instance, and it should
   * survive a reload and a trip to the duplicates screen. A setting that reset
   * every visit would be worse than no setting - the user would re-hide twelve
   * columns every time and stop bothering.
   *
   * Hiding is presentation only. The read still fetches every field, because
   * Acumatica meters API calls rather than bytes, and because a hidden column
   * has to be able to come back instantly - dropping it from $select would
   * make unhiding it cost a round trip and, worse, leave the diff and undo
   * paths comparing against data the page never loaded.
   */
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [showColumns, setShowColumns] = useState(false);
  /** Widths the user has set by dragging. Absent means "use the measured one". */
  const [widths, setWidths] = useState<Record<string, number>>({});
  /** Natural width of each column, measured from the rendered table. */
  const [measured, setMeasured] = useState<Record<string, number>>({});
  const headRef = useRef<HTMLTableSectionElement>(null);

  const columnPrefsKey =
    tenantId && entity ? `acutidy.hiddenColumns.${tenantId}.${entity}` : null;
  const widthPrefsKey =
    tenantId && entity ? `acutidy.columnWidths.${tenantId}.${entity}` : null;

  useEffect(() => {
    if (!widthPrefsKey) return;
    try {
      const saved = window.localStorage.getItem(widthPrefsKey);
      setWidths(saved ? (JSON.parse(saved) as Record<string, number>) : {});
    } catch {
      setWidths({});
    }
  }, [widthPrefsKey]);

  useEffect(() => {
    if (!columnPrefsKey) return;
    try {
      const saved = window.localStorage.getItem(columnPrefsKey);
      setHidden(new Set(saved ? (JSON.parse(saved) as string[]) : []));
    } catch {
      // A corrupt or unavailable store must not stop the grid loading; the
      // cost of ignoring it is that every column shows, which is the safe way
      // to be wrong.
      setHidden(new Set());
    }
  }, [columnPrefsKey]);

  const changeCount = useMemo(
    () => Object.values(edits).reduce((n, r) => n + Object.keys(r.fields).length, 0),
    [edits]
  );

  useEffect(() => {
    if (!api) return;
    api
      .get<{ entities: EntityDefinition[] }>('/entities')
      .then(({ entities }) => {
        setAllEntities(entities);
        setDefinition(
          entities.find((e) => e.name.toLowerCase() === entity?.toLowerCase()) ?? null
        );
      })
      .catch(() => {
        // Non-fatal: without the definition the grid falls back to whatever
        // keys the records themselves carry.
        setDefinition(null);
      });
  }, [api, entity]);

  useEffect(() => {
    if (!api || !tenantId) return;
    api
      .get<WritePolicy>(`/tenants/${tenantId}/write-policy`)
      .then(setPolicy)
      .catch(() => setPolicy(null));
  }, [api, tenantId]);

  const load = useCallback(async () => {
    if (!api || !tenantId || !entity) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        top: String(PAGE_SIZE),
        skip: String(skip),
      });
      if (appliedFilter) params.set('filter', appliedFilter);

      setResult(
        await api.get<EntityListResult>(
          `/tenants/${tenantId}/entities/${entity}?${params.toString()}`
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load records.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, entity, skip, appliedFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  // Switching entity resets the view. Without this, paging position and a
  // search typed against Customers would carry over to Vendors, where the
  // filter refers to a field that does not exist there.
  useEffect(() => {
    setSkip(0);
    setSearchText('');
    setAppliedFilter(null);
    setShowPreview(false);
    setJobResult(null);
  }, [entity]);

  // Pending edits live only in this tab's memory. Losing them to a stray
  // reload after twenty minutes of grid work would be its own bug report.
  useEffect(() => {
    if (changeCount === 0) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [changeCount]);

  /**
   * Switching entity throws away pending edits, because they live in this
   * page's state and are keyed to records of the entity being left. Losing
   * twenty minutes of grid work to a misplaced click would be indefensible,
   * so an edit in progress turns the switch into a question.
   */
  function requestSwitch(target: EntityDefinition) {
    requestLeave(target.label, `/tenants/${tenantId}/entities/${target.name}`);
  }

  /**
   * Navigates, or asks first when there are edits to lose.
   *
   * ⚠ EVERY way out of this page must go through here. beforeunload covers
   * closing the tab and requestSwitch covers the entity tabs, but an in-app
   * <Link> unmounts the page with no event to hook — so the links call this
   * instead of navigating themselves.
   */
  function requestLeave(destination: string, to: string) {
    if (!shouldConfirmLeave(changeCount)) {
      navigate(to);
      return;
    }
    setPendingLeave({ destination, go: () => navigate(to) });
  }

  function handleSearch(event: FormEvent) {
    event.preventDefault();
    const trimmed = searchText.trim();

    // Build an OData contains() over the entity's key field. Quotes are
    // doubled because that is how a literal quote is escaped in an OData
    // string — without it, a customer id containing an apostrophe produces a
    // filter Acumatica rejects.
    const keyField = definition?.keyField ?? 'CustomerID';
    setAppliedFilter(
      trimmed ? `substringof('${trimmed.replace(/'/g, "''")}', ${keyField})` : null
    );
    setSkip(0);
  }

  function editCell(
    recordId: string,
    keyValue: string,
    column: string,
    before: unknown,
    after: string
  ) {
    setEdits((current) => {
      const record = current[recordId];
      const fields = { ...(record?.fields ?? {}) };

      // Typing a value back to what it was is not a change. Dropping it here
      // keeps the count honest and stops the preview listing no-op rows — and
      // a no-op write is still a metered write against the customer's budget.
      if (after === formatCell(before)) {
        delete fields[column];
      } else {
        fields[column] = { before, after };
      }

      if (Object.keys(fields).length === 0) {
        const { [recordId]: _dropped, ...rest } = current;
        return rest;
      }
      return { ...current, [recordId]: { keyValue, fields } };
    });
  }

  function cellValue(recordId: string, column: string, original: unknown): string {
    const pending = edits[recordId]?.fields[column];
    return pending ? pending.after : formatCell(original);
  }

  // Scalar columns are declared by the API; attribute columns are whatever
  // this page of records actually carried, so they appear only on instances
  // that define them.
  const scalarColumns =
    definition?.fields ??
    (result?.records[0]
      ? Object.keys(result.records[0]).filter((k) => k !== 'id' && !isAttribute(k))
      : []);
  const columns = [...scalarColumns, ...(result?.attributeColumns ?? [])];
  const keyField = definition?.keyField ?? 'CustomerID';
  const locked = readOnlyColumns(keyField);

  /** Columns with unsaved edits. These cannot be hidden — see toggleColumn. */
  const editedColumns = useMemo(() => {
    const names = new Set<string>();
    for (const record of Object.values(edits)) {
      for (const field of Object.keys(record.fields)) names.add(field);
    }
    return names;
  }, [edits]);

  const visibleColumns = columns.filter((column) => !hidden.has(column));

  function persistHidden(next: Set<string>) {
    setHidden(next);
    if (!columnPrefsKey) return;
    try {
      window.localStorage.setItem(columnPrefsKey, JSON.stringify([...next]));
    } catch {
      // Private browsing, a full quota, a locked-down browser. The picker
      // still works for this session; only the memory of it is lost.
    }
  }

  // Measure whenever a visible column has no natural width yet — a fresh page,
  // a switched entity, or an attribute column that only exists on this
  // instance. Columns already measured are left alone, so paging does not make
  // the layout jump about.
  const needsMeasuring = visibleColumns.some((column) => measured[column] === undefined);

  useLayoutEffect(() => {
    if (!needsMeasuring || !headRef.current) return;
    const cells = headRef.current.querySelectorAll('th');
    const next: Record<string, number> = {};
    visibleColumns.forEach((column, index) => {
      const cell = cells[index];
      if (cell) next[column] = Math.ceil(cell.getBoundingClientRect().width);
    });
    setMeasured((current) => ({ ...current, ...next }));
  });

  /** What a column is rendered at: the user's width, or the measured default. */
  const columnWidth = (column: string): number =>
    widths[column] ??
    clampWidth(Math.min(measured[column] ?? DEFAULT_WIDTH_CAP, DEFAULT_WIDTH_CAP));

  function writeWidths(next: Record<string, number>) {
    if (!widthPrefsKey) return;
    try {
      window.localStorage.setItem(widthPrefsKey, JSON.stringify(next));
    } catch {
      // Same as the hidden-column store: the session still works, only the
      // memory of it is lost.
    }
  }

  function persistWidths(next: Record<string, number>) {
    setWidths(next);
    writeWidths(next);
  }

  /**
   * Drag to resize.
   *
   * Pointer events rather than mouse events, and pointer capture rather than
   * document-level listeners: capture keeps the drag attached to this handle
   * even when the pointer outruns it, which is exactly what happens when
   * someone drags a column wider in one quick motion.
   */
  function startResize(event: React.PointerEvent<HTMLSpanElement>, column: string) {
    event.preventDefault();
    event.stopPropagation();

    const handle = event.currentTarget;
    const startX = event.clientX;
    const startWidth = columnWidth(column);
    handle.setPointerCapture(event.pointerId);

    let latest = startWidth;
    let moved = false;

    const onMove = (move: PointerEvent) => {
      moved = true;
      latest = clampWidth(startWidth + (move.clientX - startX));
      setWidths((current) => mergeColumnWidth(current, column, latest));
    };

    const onUp = () => {
      handle.releasePointerCapture(event.pointerId);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);

      // A press that never moved is not a resize — it is the first or second
      // half of a double-click asking to auto-fit. Persisting here would
      // write the pre-fit width AFTER the fit had been applied, which is
      // exactly what happened: the column resized on screen and came back
      // wrong on the next visit.
      if (!moved) return;

      // Saved once at the end rather than on every pixel of the drag, and
      // from the SAME map that goes into state.
      //
      // ⚠ This used to build the saved map by spreading the render-time
      // `widths` while state was built by a functional updater. Resizing one
      // column and then another before a re-render saved a base map that never
      // held the first change, so it silently reverted on the next visit. See
      // mergeColumnWidth.
      //
      // ⚠ Writing to storage from inside the updater is deliberate, and safe
      // because the write is idempotent: React may invoke an updater more than
      // once, and storing the same JSON twice costs nothing. Reading the
      // current map any other way means keeping a second copy in sync, which
      // is how this went wrong the first time.
      setWidths((current) => {
        const next = mergeColumnWidth(current, column, latest);
        writeWidths(next);
        return next;
      });
    };

    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  }

  /** Double-click a handle: widen the column to its longest value on this page. */
  function autoFit(column: string) {
    const natural = measured[column];
    if (natural === undefined) return;
    // No cap here — an explicit "show me all of it" should show all of it.
    persistWidths({ ...widths, [column]: clampWidth(natural) });
  }

  function toggleColumn(column: string) {
    // The key field identifies the row. Hiding it would leave a grid of values
    // with nothing saying which record they belong to.
    if (column === keyField) return;
    // Hiding a column that has unsaved edits would put pending changes out of
    // sight while they still went to Acumatica on confirm. The preview would
    // list a field the user could no longer see on the grid.
    if (editedColumns.has(column)) return;

    const next = new Set(hidden);
    if (next.has(column)) next.delete(column);
    else next.add(column);
    persistHidden(next);
  }

  const diffRows = useMemo(
    () =>
      Object.entries(edits).flatMap(([recordId, record]) =>
        Object.entries(record.fields).map(([field, edit]) => ({
          recordId,
          keyValue: record.keyValue,
          field,
          before: formatCell(edit.before),
          after: edit.after,
        }))
      ),
    [edits]
  );

  /**
   * Leaves the preview or job-result screen for the grid.
   *
   * ⚠ ONE FUNCTION FOR ALL THREE EXITS, because there are three and they drift.
   * The first version of the error-clearing fix wired only the header link, so
   * leaving by the button six lines further down carried a "Failed to fetch"
   * from a refused WRITE onto a grid that had loaded perfectly — an error
   * message attached to the wrong thing entirely. Found by clicking the other
   * button; no test covered it, because the bug was in which call sites were
   * changed rather than in any rule.
   */
  function backToGrid() {
    setError(null);
    setShowPreview(false);
    setJobResult(null);
  }

  async function confirmChanges() {
    // ⚠ A REF, NOT THE `submitting` STATE, AND THIS DISTINCTION IS THE WHOLE
    // GUARD. Proven on 19 September 2026 by clicking Confirm twice in one tick
    // against production: TWO jobs were created, both wrote, and the second
    // job read the record before the first one's write landed — so the
    // customer's ERP took two writes for one intended change, with two undo
    // entries. The instance then returned `API Login Limit`, because two
    // concurrent jobs hold two of the licence's seats.
    //
    // Checking `submitting` does NOT prevent that. It is state, so both
    // handlers in the same tick close over the same pre-update value of
    // `false` and both proceed. `disabled={submitting}` does not help either:
    // React has not re-rendered yet. Only something set SYNCHRONOUSLY before
    // the first await can stop the second call, and a ref is that.
    //
    // MergePage.merge had the state check and was cited as the version to copy.
    // It has the same hole; it now uses a ref too.
    if (!api || !tenantId || !entity || diffRows.length === 0) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    setSubmitting(true);
    setError(null);
    try {
      // 202: accepted and running. The write happens in a Durable Functions
      // orchestration now, so this returns as soon as the change set is
      // recorded and the job is watched to completion below.
      const accepted = await api.post<JobResult>(`/tenants/${tenantId}/jobs`, {
        entityType: entity,
        changes: diffRows.map((row) => ({
          recordId: row.keyValue,
          recordGuid: row.recordId,
          fieldName: row.field,
          // Sent as the value the user saw. The server re-reads before writing
          // and refuses to overwrite anything that has moved since.
          beforeValue: row.before === '' ? null : row.before,
          afterValue: row.after === '' ? null : row.after,
        })),
      });

      setProgress(null);
      const result = await awaitJob(api, tenantId, accepted.job.jobId, setProgress);
      setJobResult(result);

      // Clear what the job SETTLED — which is not the same as what it wrote.
      // A resumed job comes back 'skipped' with the record already holding the
      // target value, and leaving that edit pending made the grid ask the user
      // to confirm work Acumatica had already done. A genuine conflict or
      // failure stays pending so they can see it and decide. See settledEdits.
      const landed = settledEditKeys(result.changes);
      setEdits((current) => {
        const next: Record<string, RecordEdit> = {};
        for (const [recordId, record] of Object.entries(current)) {
          const fields = Object.fromEntries(
            Object.entries(record.fields).filter(
              ([field]) => !landed.has(`${record.keyValue}:${field}`)
            )
          );
          if (Object.keys(fields).length > 0) next[recordId] = { ...record, fields };
        }
        return next;
      });

      await load();
      // A completed job that sticks is exactly what lifts the first-job cap,
      // so re-read the policy rather than making the user reload to discover it.
      setPolicy(await api.get<WritePolicy>(`/tenants/${tenantId}/write-policy`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The job could not be run.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      setProgress(null);
    }
  }

  if (jobResult) {
    const counts = jobResult.changes.reduce<Record<string, number>>((acc, c) => {
      acc[c.writeStatus] = (acc[c.writeStatus] ?? 0) + 1;
      return acc;
    }, {});

    return (
      <>
        <div className="page-header">
          <div>
            <h1>Job complete</h1>
          </div>
        </div>

        {/* ⚠ Rendered here too, not only on the grid. A write refused after
            this screen appeared — a support stop, a lapsed subscription, a
            timeout waiting for the job — otherwise set `error` on a branch
            that never displays it, so the customer saw nothing at all. */}
        <ErrorNotice message={error} />

        <div className="card">
          <p>
            <strong>{counts.written ?? 0} written</strong>
            {counts.skipped ? `, ${counts.skipped} skipped` : ''}
            {counts.failed ? `, ${counts.failed} failed` : ''}.
          </p>

          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{keyField}</th>
                  <th>Field</th>
                  <th>Result</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {jobResult.changes.map((change) => (
                  <tr key={`${change.recordId}:${change.fieldName}`}>
                    <td className="mono">{change.recordId}</td>
                    <td>
                      {fieldLabel(change.fieldName)}
                    </td>
                    <td>
                      <span className={`status-pill status-${change.writeStatus}`}>
                        {change.writeStatus}
                      </span>
                    </td>
                    <td className="wrap muted small">
                      {change.errorReason ??
                        `${change.beforeValue ?? '(empty)'} → ${change.afterValue ?? '(empty)'}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pager">
            <button type="button" onClick={backToGrid}>
              Back to grid
            </button>
          </div>
          <p className="muted small">
            Every written change is reversible from{' '}
            <Link to={`/tenants/${tenantId}/jobs`}>job history</Link>.
          </p>
        </div>
      </>
    );
  }

  if (showPreview) {
    return (
      <>
        <div className="page-header">
          <div>
            <button type="button" className="link-button" onClick={backToGrid}>
              ← Back to grid
            </button>
            <h1>Preview changes</h1>
          </div>
        </div>

        {/* ⚠ THE SCREEN WHERE IT MATTERS MOST. Confirm sets `error` and stays
            here, so without this the button simply re-enables and nothing is
            said — which reads as "nothing happened" when a job may already
            exist. */}
        <ErrorNotice message={error} />

        <div className="card">
          <p className="muted">
            {diffRows.length} change{diffRows.length === 1 ? '' : 's'} across{' '}
            {Object.keys(edits).length} record{Object.keys(edits).length === 1 ? '' : 's'}.
            Nothing has been sent to Acumatica.
          </p>

          <WriteBlockedNotice
            policy={policy}
            tenantId={tenantId!}
            place="edits"
            onNavigate={requestLeave}
          />

          {policy?.firstJob && !policy.disabled && (
            <div className="notice warn">
              <strong>First job on this instance.</strong> It is limited to{' '}
              {policy.maxChanges} changes while we confirm writes land where you
              expect — Acumatica gives us no way to tell a sandbox from production,
              and this bounds the cost of a wrong instance. Once a job completes and
              stays, the limit lifts automatically.
              {diffRows.length > policy.maxChanges && (
                <>
                  {' '}
                  You have {diffRows.length}; deselect or discard{' '}
                  {diffRows.length - policy.maxChanges} before confirming.
                </>
              )}
            </div>
          )}

          <div className="table-scroll">
            <table className="data-table diff-table">
              <thead>
                <tr>
                  <th>{keyField}</th>
                  <th>Field</th>
                  <th>Before</th>
                  <th>After</th>
                </tr>
              </thead>
              <tbody>
                {diffRows.map((row) => (
                  <tr key={`${row.recordId}:${row.field}`}>
                    <td className="mono">{row.keyValue}</td>
                    <td>
                      {fieldLabel(row.field)}
                      {isAttribute(row.field) && <span className="attribute-tag">attribute</span>}
                      {isNested(row.field) && <span className="nested-tag">contact</span>}
                    </td>
                    <td className="diff-before">{row.before || <em className="muted">empty</em>}</td>
                    <td className="diff-after">{row.after || <em className="muted">empty</em>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pager">
            <button type="button" onClick={backToGrid}>
              Back to grid
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => void confirmChanges()}
              // ⚠ A policy that failed to load blocks. It used to read as
              // writable, so the product invited a write the server would
              // refuse. See writeGate.
              disabled={submitting || !canConfirm({ policy, changeCount: diffRows.length })}
            >
              {submitting
                ? progress && progress.total > 0
                  ? `Writing… ${progress.done} of ${progress.total}`
                  : 'Writing…'
                : `Confirm ${diffRows.length} change${diffRows.length === 1 ? '' : 's'}`}
            </button>
          </div>
          <p className="muted small">
            Each record is re-read immediately before it is written. Anything that
            changed in Acumatica since you edited it is skipped, not overwritten, and
            every write that lands is recorded in the undo log.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <button
            type="button"
            className="link-button muted small"
            onClick={() => requestLeave('the instance page', `/tenants/${tenantId}`)}
          >
            ← Instance
          </button>
          <h1>{definition?.label ?? entity}</h1>
          {/* Straight across to the other entities, without going back via
              the instance page. Cleaning up master data means moving between
              Customers, Vendors and Stock Items constantly. */}
          {allEntities.length > 1 && (
            <nav className="entity-switch">
              {allEntities.map((other) => (
                <button
                  key={other.name}
                  type="button"
                  className={other.name === definition?.name ? 'entity-tab current' : 'entity-tab'}
                  aria-current={other.name === definition?.name ? 'page' : undefined}
                  onClick={() => requestSwitch(other)}
                  disabled={other.name === definition?.name}
                >
                  {other.label}
                </button>
              ))}
            </nav>
          )}
        </div>
        {changeCount > 0 && (
          <div className="toolbar">
            <span className="change-count">
              {changeCount} change{changeCount === 1 ? '' : 's'} pending
            </span>
            <button type="button" className="primary" onClick={() => setShowPreview(true)}>
              Preview changes
            </button>
            <button type="button" className="link-button" onClick={() => setEdits({})}>
              Discard
            </button>
          </div>
        )}
      </div>

      {/*
        ⚠ SAID HERE, BEFORE THE WORK, not only in the preview panel. This used
        to appear for the first time after a customer had made every edit they
        intended to make — the product took twenty minutes of typing and then
        told them it could not keep any of it. The preview copy stays too: this
        one scrolls away, and the commit moment is where "is my work lost?"
        actually gets asked.
      */}
      <WriteBlockedNotice
            policy={policy}
            tenantId={tenantId!}
            place="edits"
            onNavigate={requestLeave}
          />

      <div className="toolbar">
        <button
          type="button"
          className="button-link"
          onClick={() =>
            requestLeave(
              'duplicate review',
              `/tenants/${tenantId}/entities/${entity}/duplicates`
            )
          }
        >
          Find duplicates
        </button>
      </div>

      <div className="toolbar">
        <button
          type="button"
          onClick={() => setShowColumns((open) => !open)}
          aria-expanded={showColumns}
        >
          Columns
          {hidden.size > 0 && (
            <span className="count-badge">{columns.length - hidden.size}/{columns.length}</span>
          )}
        </button>
        {hidden.size > 0 && !showColumns && (
          <span className="muted small">
            {hidden.size} column{hidden.size === 1 ? '' : 's'} hidden
          </span>
        )}
      </div>

      {showColumns && (
        <div className="card column-picker">
          <div className="toolbar">
            <button type="button" onClick={() => persistHidden(new Set())}>
              Show all
            </button>
            <button
              type="button"
              onClick={() =>
                // Everything except the key, anything being edited, and the
                // record's own name - hiding down to a single column of ids
                // is never what anyone means by "hide all".
                persistHidden(
                  new Set(
                    columns.filter(
                      (column, index) =>
                        column !== keyField && index !== 1 && !editedColumns.has(column)
                    )
                  )
                )
              }
            >
              Hide all
            </button>
            <button
              type="button"
              onClick={() => persistWidths({})}
              disabled={Object.keys(widths).length === 0}
            >
              Reset widths
            </button>
            <button type="button" className="link-button" onClick={() => setShowColumns(false)}>
              Done
            </button>
          </div>

          <div className="column-list">
            {columns.map((column) => {
              const isKey = column === keyField;
              const isEdited = editedColumns.has(column);
              const fixed = isKey || isEdited;
              return (
                <label
                  key={column}
                  className={fixed ? 'column-option fixed' : 'column-option'}
                  title={
                    isKey
                      ? 'The key identifies the row and always shows.'
                      : isEdited
                        ? 'This column has unsaved changes, so it stays visible.'
                        : fieldTitle(column)
                  }
                >
                  <input
                    type="checkbox"
                    checked={!hidden.has(column)}
                    disabled={fixed}
                    onChange={() => toggleColumn(column)}
                  />
                  <span>
                    {fieldLabel(column)}
                    {isAttribute(column) && <span className="attribute-tag">attribute</span>}
                    {isNested(column) && <span className="nested-tag">contact</span>}
                    {isEdited && <span className="muted small"> · edited</span>}
                  </span>
                </label>
              );
            })}
          </div>
          <p className="muted small">
            Hiding a column only changes this grid — every field is still read, and
            nothing you have already edited can be hidden. Your choice is remembered
            for this instance.
          </p>
        </div>
      )}

      <form className="toolbar" onSubmit={handleSearch}>
        <input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={`Search by ${keyField}…`}
          aria-label="Search"
        />
        <button type="submit">Search</button>
        {appliedFilter && (
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setSearchText('');
              setAppliedFilter(null);
              setSkip(0);
            }}
          >
            Clear
          </button>
        )}
      </form>

      {pendingLeave && (
        <div className="notice warn">
          <strong className="notice-title">
            {pendingEditsWarning(changeCount, pendingLeave.destination).title}
          </strong>
          <p className="notice-body">
            {pendingEditsWarning(changeCount, pendingLeave.destination).body}
          </p>
          <div className="toolbar">
            <button type="button" onClick={() => setPendingLeave(null)}>
              Stay here
            </button>
            <button
              type="button"
              onClick={() => {
                const leaving = pendingLeave;
                setEdits({});
                setPendingLeave(null);
                leaving.go();
              }}
            >
              Discard and leave
            </button>
          </div>
        </div>
      )}

      <ErrorNotice message={error} onRetry={() => void load()} />

      {loading && (
        <>
          <p className="muted">Loading…</p>
          <SlowHint>
            Still working. This reads directly from your Acumatica instance, so it
            depends on how quickly that responds — and if the instance is offline,
            it will time out and say so rather than wait indefinitely.
          </SlowHint>
        </>
      )}

      {!loading && result && result.records.length === 0 && (
        <p className="muted">No records matched.</p>
      )}

      {!loading && result && result.records.length > 0 && (
        <>
          <div className="table-scroll">
            {/* Fixed layout is what makes an explicit width mean something —
                under auto layout the browser treats it as a suggestion and
                re-widens the column to fit its content, so shrinking one would
                not work. */}
            <table
              className={
                needsMeasuring ? 'data-table resizable measuring' : 'data-table resizable'
              }
            >
              {!needsMeasuring && (
                <colgroup>
                  {visibleColumns.map((column) => (
                    <col key={column} style={{ width: `${columnWidth(column)}px` }} />
                  ))}
                </colgroup>
              )}
              <thead ref={headRef}>
                <tr>
                  {visibleColumns.map((column) => (
                    <th
                      key={column}
                      className={isAttribute(column) ? 'th-attribute' : undefined}
                      title={fieldTitle(column)}
                    >
                      {fieldLabel(column)}
                      {isAttribute(column) && <span className="attribute-tag">attribute</span>}
                      {isNested(column) && <span className="nested-tag">contact</span>}
                      {locked.has(column) && <span className="th-readonly">read-only</span>}
                      <span
                        className="col-resize"
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Resize ${fieldLabel(column)}`}
                        title="Drag to resize · double-click to fit the widest value"
                        onPointerDown={(e) => startResize(e, column)}
                        onDoubleClick={() => autoFit(column)}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.records.map((record, index) => {
                  const recordId = String(record.id ?? index);
                  const keyValue = formatCell(record[keyField]);

                  return (
                    <tr key={recordId}>
                      {visibleColumns.map((column) => {
                        if (locked.has(column)) {
                          const raw = formatCell(record[column]);
                          return (
                            <td
                              key={column}
                              className="cell-locked"
                              title={readOnlyCellTitle(raw)}
                            >
                              {formatReadOnlyCell(raw)}
                            </td>
                          );
                        }

                        const changed = Boolean(edits[recordId]?.fields[column]);

                        // During the measuring pass the cell renders as text.
                        // An <input> carries its own intrinsic width, so
                        // measuring one would record how wide a text box likes
                        // to be rather than how wide the DATA is — every
                        // column came out the same generous size, Status
                        // included.
                        if (needsMeasuring) {
                          return (
                            <td key={column}>{cellValue(recordId, column, record[column])}</td>
                          );
                        }

                        return (
                          <td key={column} className={changed ? 'cell-changed' : undefined}>
                            <input
                              className="cell-input"
                              value={cellValue(recordId, column, record[column])}
                              aria-label={`${keyValue} ${column}`}
                              onChange={(e) =>
                                editCell(
                                  recordId,
                                  keyValue,
                                  column,
                                  record[column],
                                  e.target.value
                                )
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="pager">
            <button
              type="button"
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={skip === 0}
            >
              Previous
            </button>
            <span className="muted small">
              {/* Contract REST returns no total count, so the range is stated
                  rather than invented as "x of y". */}
              Records {skip + 1}–{skip + result.records.length}
            </span>
            <button
              type="button"
              onClick={() => setSkip(skip + PAGE_SIZE)}
              disabled={!result.hasMore}
            >
              Next
            </button>
          </div>
        </>
      )}
    </>
  );
}
